import * as React from 'react';
import { X } from 'lucide-react';
import { cn } from '../utils';

/**
 * Diálogo acessível sobre o <dialog> nativo (foco preso, Esc fecha, backdrop).
 * No celular abre como folha a partir de baixo.
 */
export function Dialog({
  aberto,
  aoFechar,
  titulo,
  descricao,
  children,
  className,
}: {
  aberto: boolean;
  aoFechar: () => void;
  titulo: string;
  descricao?: string;
  children: React.ReactNode;
  className?: string;
}) {
  const ref = React.useRef<HTMLDialogElement>(null);
  const id = React.useId();
  React.useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (aberto && !d.open) {
      if (typeof d.showModal === 'function') d.showModal();
      else d.setAttribute('open', '');
    }
    if (!aberto && d.open) {
      if (typeof d.close === 'function') d.close();
      else d.removeAttribute('open');
    }
  }, [aberto]);
  return (
    <dialog
      ref={ref}
      aria-labelledby={`${id}-t`}
      onClose={aoFechar}
      onCancel={(e) => {
        e.preventDefault();
        aoFechar();
      }}
      onClick={(e) => {
        if (e.target === ref.current) aoFechar();
      }}
      className={cn(
        'm-0 mt-auto w-full max-w-none rounded-t-2xl border bg-card p-0 text-card-foreground shadow-xl backdrop:bg-black/40 sm:m-auto sm:max-w-lg sm:rounded-2xl',
        className,
      )}
    >
      {aberto && (
        <div className="flex max-h-[85dvh] flex-col">
          <div className="flex items-start justify-between gap-4 border-b p-5">
            <div>
              <h2 id={`${id}-t`} className="text-lg font-semibold">
                {titulo}
              </h2>
              {descricao && <p className="mt-1 text-sm text-muted-foreground">{descricao}</p>}
            </div>
            <button
              type="button"
              onClick={aoFechar}
              aria-label="Fechar"
              className="rounded-md p-1 text-muted-foreground hover:bg-accent"
            >
              <X className="size-5" />
            </button>
          </div>
          <div className="overflow-y-auto p-5">{children}</div>
        </div>
      )}
    </dialog>
  );
}
